/**
 * DeployStatusBanner
 * ─────────────────────────────────────────────────────────────────────
 * Shown to super_admin users when the last auto-deploy failed — so Sam
 * knows immediately without having to SSH into the server and check logs.
 *
 * The banner polls GET /api/system/deploy-status every 5 minutes. When
 * status is 'failed', it renders a sticky red warning at the top of the
 * portal shell (above the masquerade / customer-context banners).
 *
 * The user can dismiss it per-session (sessionStorage). It re-appears on
 * the next page load if the deploy is still broken.
 *
 * Why polling (not websocket)? The deploy script writes a flat JSON file;
 * there is no push channel from the server on failure. Five-minute polling
 * is cheap and fast enough — a broken deploy is noticed within 5 minutes,
 * not within a second.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, X, RefreshCw } from 'lucide-react';
import { useAuth } from '@/lib/auth-store';
import { apiFetch } from '@/api/client';
import { cn } from '@/lib/cn';

interface DeployStatus {
  status: 'success' | 'failed' | 'unknown';
  message: string;
  sha: string | null;
  timestamp: string | null;
}

const DISMISS_KEY = 'ivrello:deploy-status-dismissed-sha';

async function fetchDeployStatus(): Promise<DeployStatus> {
  return apiFetch<DeployStatus>('/api/system/deploy-status');
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

export function DeployStatusBanner() {
  const user = useAuth((s) => s.user);
  const [dismissed, setDismissed] = useState<string | null>(
    () => sessionStorage.getItem(DISMISS_KEY),
  );

  const { data, refetch, isRefetching } = useQuery({
    queryKey: ['deploy-status'],
    queryFn: fetchDeployStatus,
    // Only poll for super_admin — the endpoint rejects other roles anyway.
    enabled: user?.role === 'super_admin',
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    staleTime: 4 * 60 * 1000,
    retry: false,
  });

  // Not super_admin, or no data yet, or deploy is healthy.
  if (user?.role !== 'super_admin') return null;
  if (!data || data.status !== 'failed') return null;

  // User dismissed this exact failure (matched by SHA or timestamp).
  const dismissKey = data.sha ?? data.timestamp ?? 'unknown';
  if (dismissed === dismissKey) return null;

  function handleDismiss() {
    sessionStorage.setItem(DISMISS_KEY, dismissKey);
    setDismissed(dismissKey);
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="sticky top-0 z-30 bg-red-600 text-white shadow-[0_2px_0_0_#b91c1c]"
    >
      {/* Diagonal-stripe texture — visually signals "system problem" */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.8) 8px 9px)',
        }}
      />

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between gap-3 py-2.5">

          {/* Icon + message */}
          <div className="flex items-start gap-3 min-w-0">
            <span
              aria-hidden
              className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm bg-white/15 ring-1 ring-white/25"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-80 leading-none">
                Auto-deploy failed
              </div>
              <div className="mt-1 text-sm font-semibold leading-snug">
                {data.message || 'The last deploy attempt failed — backend may be running stale code.'}
                {data.timestamp && (
                  <span className="ms-2 text-xs font-normal opacity-75">
                    ({formatTimestamp(data.timestamp)})
                  </span>
                )}
              </div>
              {data.sha && (
                <div className="mt-0.5 text-[10px] font-mono opacity-60 tracking-wide">
                  commit {data.sha.slice(0, 8)}
                </div>
              )}
              <div className="mt-1 text-xs opacity-75">
                Check the server: <code className="font-mono bg-white/15 px-1 py-0.5 rounded text-[10px]">journalctl -u ivrello-deploy.service -n 50</code>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-shrink-0 items-center gap-1.5 mt-0.5">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isRefetching}
              title="Re-check deploy status"
              className={cn(
                'inline-flex items-center gap-1 h-7 px-2 rounded border border-white/30 bg-white/10 text-xs font-medium',
                'hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <RefreshCw className={cn('h-3 w-3', isRefetching && 'animate-spin')} />
              <span>Recheck</span>
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              title="Dismiss this alert for this session"
              className="inline-flex items-center justify-center h-7 w-7 rounded border border-white/30 bg-white/10 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label="Dismiss deploy failure alert"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
